import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TIP_MAX_W = 260; // keep in sync with .infotip-body max-width

// A "?" glyph that reveals a short static explanation on hover/focus. The bubble
// renders through a PORTAL to document.body with fixed positioning (same pattern as
// HeaderFilter's popover), so it escapes overflow:hidden cards, scrolling table
// wrappers, and sticky stacking contexts, and is clamped to the viewport — it can
// never be cropped. Closes on leave/blur and on any scroll/resize (a fixed bubble
// would otherwise drift away from its icon). stopPropagation keeps the icon from
// triggering clickable parents (e.g. the collapsible group headers).
export default function InfoTip({ text }: { text: string }) {
  const iconRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function show() {
    const r = iconRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({
      top: Math.min(r.bottom + 6, window.innerHeight - 120),
      left: Math.max(8, Math.min(r.left + r.width / 2 - TIP_MAX_W / 2, window.innerWidth - TIP_MAX_W - 8)),
    });
  }
  const hide = () => setPos(null);

  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [pos]);

  return (
    <span ref={iconRef} className="infotip" tabIndex={0} aria-label={text} role="button"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === "Escape" && pos) { e.stopPropagation(); hide(); } }}
      onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      <span className="infotip-icon" aria-hidden="true">?</span>
      {pos && createPortal(
        <span className="infotip-body" style={{ top: pos.top, left: pos.left }}>{text}</span>,
        document.body
      )}
    </span>
  );
}
